import { useEffect, useState, useMemo } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Shield, Search, Save, ListChecks, Sparkles, Briefcase } from "lucide-react";
import { toast } from "sonner";

const KIP_SECTIONS = [
  { id: "updates", label: "Regulatory Updates" },
  { id: "documents", label: "Knowledge Repository" },
  { id: "markets", label: "Markets & Companies" },
  { id: "centre", label: "Financial Centre" },
  { id: "assistant", label: "AI Assistant" },
  { id: "bulletin", label: "Bulletin Builder" },
];

const KIP_TABS = [
  { id: "audit", label: "Audit & Accounting" },
  { id: "mca", label: "MCA / Company Law" },
  { id: "income_tax", label: "Income Tax" },
  { id: "gst", label: "GST" },
  { id: "rbi_fema", label: "RBI & FEMA" },
  { id: "sebi", label: "SEBI" },
  { id: "valuation", label: "Valuation" },
  { id: "insolvency", label: "IBC" },
  { id: "judicial", label: "Judicial" },
  { id: "others", label: "Others" },
];

export default function AccessManagement() {
  const [users, setUsers] = useState([]);
  const [toolCatalog, setToolCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [editUser, setEditUser] = useState(null);

  const loadUsers = async () => {
    const { data } = await api.get("/admin/access");
    setUsers(data);
  };
  useEffect(() => {
    loadUsers();
    api.get("/tools/all").then(({ data }) => setToolCatalog(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, search]);

  return (
    <div className="space-y-6" data-testid="access-page">
      <div>
        <div className="eyebrow">Office Admin</div>
        <h1 className="font-sans text-3xl flex items-center gap-2"><Shield className="h-6 w-6"/>Access Management</h1>
        <p className="text-sm text-slate-600 mt-1">Partner/Admin can grant Section 2 (Knowledge Intelligence) and Section 3 (Tools) access — locked by default for all other roles.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search user…" className="pl-9"/>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="access-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">KIP</th>
              <th className="text-left p-3">Tools</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const k = u.permissions?.kip || {};
              const t = u.permissions?.tools || {};
              const fullAccess = u.role === "partner" || u.role === "admin";
              return (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`access-row-${u.id}`}>
                  <td className="p-3">
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="p-3"><Badge variant="outline" className="capitalize">{u.role}</Badge></td>
                  <td className="p-3"><Badge className={u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}>{u.status}</Badge></td>
                  <td className="p-3">
                    {fullAccess ? <Badge className="bg-blue-100 text-blue-700">Full</Badge> :
                      k.enabled ? <Badge className="bg-emerald-100 text-emerald-700">{(k.sections || []).length}/6 sections</Badge> :
                      <Badge className="bg-slate-100 text-slate-600">Locked</Badge>}
                  </td>
                  <td className="p-3">
                    {fullAccess ? <Badge className="bg-blue-100 text-blue-700">Full</Badge> :
                      t.enabled ? <Badge className="bg-emerald-100 text-emerald-700">{(t.allowed_tool_ids || []).length} tools</Badge> :
                      <Badge className="bg-slate-100 text-slate-600">Locked</Badge>}
                  </td>
                  <td className="p-3 text-right">
                    {!fullAccess && <Button size="sm" variant="outline" onClick={() => setEditUser(u)} data-testid={`edit-${u.id}`}>Manage</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {editUser && <AccessDialog user={editUser} toolCatalog={toolCatalog} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); loadUsers(); }}/>}
    </div>
  );
}

function AccessDialog({ user, toolCatalog, onClose, onSaved }) {
  const initialPerms = user.permissions || {};
  const initialKip = initialPerms.kip || {};
  const initialTools = initialPerms.tools || {};

  const [kipEnabled, setKipEnabled] = useState(!!initialKip.enabled);
  const [kipSections, setKipSections] = useState(new Set(initialKip.sections || KIP_SECTIONS.map(s => s.id)));
  const [kipTabs, setKipTabs] = useState(new Set(initialKip.kip_tabs || KIP_TABS.map(t => t.id)));
  const [toolsEnabled, setToolsEnabled] = useState(!!initialTools.enabled);
  const [allowedTools, setAllowedTools] = useState(new Set(initialTools.allowed_tool_ids || []));

  const toggle = (set, setter, id) => {
    const s = new Set(set);
    s.has(id) ? s.delete(id) : s.add(id);
    setter(s);
  };

  const save = async () => {
    try {
      await api.patch(`/admin/access/${user.id}`, {
        kip_enabled: kipEnabled,
        kip_sections: Array.from(kipSections),
        kip_tabs: Array.from(kipTabs),
        tools_enabled: toolsEnabled,
        allowed_tool_ids: Array.from(allowedTools),
      });
      toast.success("Access updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const groupToggle = (group, allEnabled) => {
    const s = new Set(allowedTools);
    group.tools.forEach((t) => { allEnabled ? s.delete(t.id) : s.add(t.id); });
    setAllowedTools(s);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="access-dialog">
        <DialogHeader>
          <DialogTitle>Manage Access — {user.name}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="kip">
          <TabsList>
            <TabsTrigger value="kip" data-testid="tab-kip"><Sparkles className="h-3 w-3 mr-1"/>Knowledge Intelligence</TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools"><Briefcase className="h-3 w-3 mr-1"/>Tools</TabsTrigger>
          </TabsList>
          <TabsContent value="kip" className="space-y-4">
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded p-3">
              <Checkbox checked={kipEnabled} onCheckedChange={setKipEnabled} data-testid="kip-toggle"/>
              <div>
                <div className="font-medium">Enable Knowledge Intelligence access</div>
                <div className="text-xs text-slate-600">When off, user cannot access any KIP feature.</div>
              </div>
            </div>
            {kipEnabled && (<>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Sections</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {KIP_SECTIONS.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded p-2 hover:bg-slate-50">
                      <Checkbox checked={kipSections.has(s.id)} onCheckedChange={() => toggle(kipSections, setKipSections, s.id)} data-testid={`kip-sec-${s.id}`}/>
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Regulatory Tabs (Updates / Documents)</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {KIP_TABS.map((t) => (
                    <label key={t.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded p-2 hover:bg-slate-50">
                      <Checkbox checked={kipTabs.has(t.id)} onCheckedChange={() => toggle(kipTabs, setKipTabs, t.id)}/>
                      {t.label}
                    </label>
                  ))}
                </div>
              </div>
            </>)}
          </TabsContent>
          <TabsContent value="tools" className="space-y-4">
            <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded p-3">
              <Checkbox checked={toolsEnabled} onCheckedChange={setToolsEnabled} data-testid="tools-toggle"/>
              <div>
                <div className="font-medium">Enable Tools access</div>
                <div className="text-xs text-slate-600">Grant per-tool. User sees only allowed tools.</div>
              </div>
            </div>
            {toolsEnabled && toolCatalog.map((g) => {
              const groupTools = g.tools.map((t) => t.id);
              const allOn = groupTools.every((id) => allowedTools.has(id));
              const someOn = groupTools.some((id) => allowedTools.has(id));
              return (
                <div key={g.group} className="border border-slate-200 rounded">
                  <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                    <div className="font-semibold text-sm">{g.group} <span className="text-xs text-slate-500 ml-1">({groupTools.filter((id) => allowedTools.has(id)).length}/{groupTools.length})</span></div>
                    <Button size="sm" variant="ghost" onClick={() => groupToggle(g, allOn)} data-testid={`group-toggle-${g.group.replace(/\W+/g,'-')}`}>
                      {allOn ? "Deselect all" : someOn ? "Select all" : "Select all"}
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-2">
                    {g.tools.map((t) => (
                      <label key={t.id} className="flex items-start gap-2 text-sm border border-slate-100 rounded p-2 hover:bg-slate-50">
                        <Checkbox checked={allowedTools.has(t.id)} onCheckedChange={() => toggle(allowedTools, setAllowedTools, t.id)} data-testid={`tool-${t.id}`}/>
                        <div>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-xs text-slate-500">{t.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="access-save"><Save className="h-4 w-4 mr-1"/>Save Access</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
